import { Controller } from 'stimulus'

// Modal de composicao do combo (feature combo-ifood-restaurant, RN-02/RN-03/RN-05/RN-07).
// Depende dos globais do sprockets: jQuery (select2, cocoon) e `initProductSkusSearch`
// (app/assets/javascripts/product_sku_search.js). Sem eles (ex.: Jest) o controller so
// expoe os metodos de tela, que nao precisam de jQuery.
const SKU_SELECT = 'select[data-attr="combo_product_sku_id"]'
const GROUP = '[data-attr="combo_group"]'
const ACTIVE_INPUT = '[data-target="product-combo.active"]'

function jQueryGlobal() {
  return typeof window !== 'undefined' ? window.jQuery || window.$ : undefined
}

export default class extends Controller {
  static targets = ['priceTable']

  connect() {
    const $ = jQueryGlobal()

    // O Semantic UI move o modal para o dimmer do body ao abrir; o Stimulus reconecta o
    // controller e os handlers do jQuery nao podem ser registrados duas vezes.
    if (!$ || this.element.dataset.productComboReady) return
    this.element.dataset.productComboReady = 'true'

    this.element.querySelectorAll(SKU_SELECT).forEach(select => this.initSkuSearch(select))

    $(this.element).on('cocoon:after-insert', (_event, inserted) => {
      $(inserted)
        .find(SKU_SELECT)
        .each((_index, select) => this.initSkuSearch(select))
      this.renumber()
    })
    $(this.element).on('cocoon:after-remove', (_event, removed) => {
      this.warnIfGroupWithoutActiveOption($(removed).closest(GROUP)[0])
      this.renumber()
    })
    this.renumber()
    // Evento do select2 e disparado via jQuery: nao chega ao addEventListener do Stimulus.
    $(this.element).on('select2:select', SKU_SELECT, event => this.skuSelected(event))
  }

  // A ordem da tela e o que vale: `sequence` de grupos e opcoes vira o `index` no cardapio do
  // iFood. Os hiddens nascem vazios nas linhas novas, entao sao renumerados pela ordem do DOM a
  // cada insercao/remocao e ao confirmar o modal; linhas marcadas com `_destroy` ficam de fora.
  renumber() {
    const kept = element => {
      const destroy = [...element.querySelectorAll('input[name$="[_destroy]"]')].find(
        input => input.closest('.nested-fields') === element
      )

      return !destroy || !['1', 'true'].includes(destroy.value)
    }
    const sequenceOf = element =>
      [...element.querySelectorAll('input[name$="[sequence]"]')].find(
        input => input.closest('.nested-fields') === element
      )

    const groups = [...this.element.querySelectorAll(GROUP)].filter(kept)

    groups.forEach((group, groupIndex) => {
      const groupSequence = sequenceOf(group)
      if (groupSequence) groupSequence.value = groupIndex

      const rows = [...group.querySelectorAll('tbody[data-attr="combo_items"] tr.nested-fields')].filter(kept)

      rows.forEach((row, rowIndex) => {
        const rowSequence = sequenceOf(row)
        if (rowSequence) rowSequence.value = rowIndex
      })
    })
  }

  initSkuSearch(select) {
    const $ = jQueryGlobal()

    if (!$ || typeof window.initProductSkusSearch !== 'function') return
    if ($(select).data('select2')) return

    window.initProductSkusSearch(select)
  }

  // RN-03: ao escolher o SKU, mostra a miniatura e pre-preenche o preco (`sale_price`,
  // liquido de desconto) da tabela escolhida. Sem tabela, o campo fica como esta; com tabela
  // e sem preco, o campo e limpo (obrigatorio) para nao herdar o preco do SKU anterior da linha.
  async skuSelected(event) {
    const data = (event.params && event.params.data) || {}
    const row = event.target.closest('tr')

    if (!row) return

    const thumb = row.querySelector('[data-target="product-combo.thumb"]')
    if (thumb && data.main_thumbnail_url) thumb.src = data.main_thumbnail_url

    const priceTableId = this.hasPriceTableTarget ? this.priceTableTarget.value : ''
    if (!priceTableId || !data.id) return

    const salePrice = await this.fetchComboPrice(data.id, priceTableId)
    const priceInput = row.querySelector('[data-target="product-combo.price"]')

    if (priceInput) priceInput.value = salePrice == null ? '' : salePrice
  }

  async fetchComboPrice(productSkuId, priceTableId) {
    const url = `/product_skus/${productSkuId}/combo_price?price_table_id=${encodeURIComponent(priceTableId)}`
    const response = await fetch(url, { headers: { Accept: 'application/json' }, credentials: 'same-origin' })

    if (!response.ok) return null

    const { sale_price: salePrice } = await response.json()

    return salePrice
  }

  // RN-07: play/pause da opcao. Pausar a ultima opcao ativa de grupo obrigatorio e
  // permitido, mas a tela avisa que o combo ficara indisponivel.
  toggleActive(event) {
    const button = event.currentTarget
    const row = button.closest('tr')
    const input = row.querySelector(ACTIVE_INPUT)
    const icon = button.querySelector('i')
    const nowActive = input.value !== 'true'

    input.value = nowActive
    row.classList.toggle('ch-table-row--disabled', !nowActive)
    button.title = nowActive ? button.dataset.titlePause : button.dataset.titleResume

    if (icon) {
      icon.classList.toggle('fa-pause', nowActive)
      icon.classList.toggle('fa-play', !nowActive)
    }

    this.warnIfGroupWithoutActiveOption(row.closest(GROUP))
  }

  minChanged(event) {
    this.warnIfGroupWithoutActiveOption(event.target.closest(GROUP))
  }

  warnIfGroupWithoutActiveOption(group) {
    if (!group) return

    const minInput = group.querySelector('input[name$="[min_quantity]"]')
    const min = parseInt((minInput && minInput.value) || '0', 10)
    const warning = group.querySelector('[data-attr="combo-group-warning"]')

    if (!warning) return

    warning.hidden = !(min >= 1 && this.activeOptions(group).length === 0)
  }

  // Opcoes ativas que ainda contam: ignora linhas removidas (cocoon esconde a linha e marca `_destroy`).
  activeOptions(group) {
    return [...group.querySelectorAll(ACTIVE_INPUT)].filter(input => {
      const row = input.closest('tr')
      const destroy = row && row.querySelector('input[name$="[_destroy]"]')
      const removed = destroy && ['1', 'true'].includes(destroy.value)

      return input.value === 'true' && !removed
    })
  }

  // RN-05: exatamente um grupo principal por combo.
  toggleMain(event) {
    if (!event.target.checked) return

    this.element.querySelectorAll('input[type="checkbox"][name$="[main]"]').forEach(checkbox => {
      if (checkbox !== event.target) checkbox.checked = false
    })
  }
}
